import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { cloneObj, ellipsizeThis, queryTillSuccess, toLocaleDecimal } from '../../common/utils';
import Spinner from '../../components/Spinner';
import { MEVSummaryDataResponse } from '../../types';
import './styles.scss';
import Mustache from 'mustache';
import { SortBy } from './types';
import moment from 'moment';

const txQuery = `WITH 
prices AS (
  SELECT
  	TRUNC(recorded_at, 'day') as date,
  	AVG(price) as price
  FROM
  	osmosis.core.dim_prices
  WHERE
  	symbol = 'ATOM'
  GROUP BY date
),

token_prices AS (
SELECT
  s.date,
  NVL(l.project_name, s.denom) as symbol,
  s.atom_ratio,
  p.price,
  atom_ratio * IFF(price is not null, price, 0) AS price_usd
FROM (
SELECT 
  block_timestamp::date as date,
  CASE WHEN from_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2'
  THEN to_currency
  ELSE from_currency
  END as denom,
  MEDIAN(
    CASE WHEN from_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2'
    THEN (from_amount / POW(10, NVL(from_decimal, 6))) / (to_amount / POW(10, NVL(to_decimal, 6)))
    ELSE (to_amount / POW(10, NVL(to_decimal, 6))) / (from_amount / POW(10, NVL(from_decimal, 6)))
    END
  ) as atom_ratio
FROM osmosis.core.fact_swaps
WHERE 
  (from_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2') OR 
  (to_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2' AND block_timestamp >= '2022-06-21') -- get to only when after 2021-06-22 cause of inaccuracies in swap ratio
GROUP BY date, denom
) s
LEFT JOIN prices p
ON p.date = s.date
LEFT JOIN osmosis.core.dim_labels l
ON l.address = s.denom
),
  
pool_ids AS (
SELECT 
  block_timestamp as created_at,
  tx_id as pool_created_at_tx_id,
  MAX(IFF(attribute_key = 'pool_id', attribute_value, '')) as pool_id,
  MAX(IFF(attribute_key = 'recipient' AND LEN(attribute_value) > 43, attribute_value, '')) as pool_address
FROM osmosis.core.fact_msg_attributes
WHERE tx_id IN (
  SELECT tx_id
  FROM osmosis.core.fact_msg_attributes
  WHERE msg_type = 'pool_created'
)
GROUP BY block_timestamp, tx_id
),
  
v2_swap AS (
SELECT
  block_timestamp,
  tx_id,
  swapper,
  msg_group,
  COUNT(DISTINCT pool_id) as n_pools,
  MAX(CASE WHEN msg_index = first_index THEN from_amount ELSE 0 END) as from_amount,
  MAX(CASE WHEN msg_index = first_index THEN from_denom ELSE '' END) as from_denom,
  MAX(CASE WHEN msg_index = last_index THEN to_amount ELSE 0 END) as to_amount,
  MAX(CASE WHEN msg_index = last_index THEN to_denom ELSE '' END) as to_denom
FROM (
  	SELECT
      block_timestamp,
      tx_id,
      msg_group,
      msg_index,
  	  FIRST_VALUE(msg_index) OVER (PARTITION BY msg_group, tx_id ORDER BY msg_index) as first_index,
  	  LAST_VALUE(msg_index) OVER (PARTITION BY msg_group, tx_id ORDER BY msg_index) as last_index,
      MAX(IFF(attribute_key = 'sender', attribute_value, '')) as swapper,
      MAX(IFF(attribute_key = 'pool_id', amount::integer, -1))::integer as pool_id,
      MAX(IFF(attribute_key = 'tokens_in', amount::integer, -1))::integer as from_amount,
      MAX(IFF(attribute_key = 'tokens_in', denom, '')) as from_denom,
      MAX(IFF(attribute_key = 'tokens_out', amount::integer, -1))::integer as to_amount,
      MAX(IFF(attribute_key = 'tokens_out', denom, '')) as to_denom
    FROM (
      SELECT 
        block_timestamp,
        tx_id,
      	msg_group,
        msg_index,
      	attribute_key,
      	attribute_value,
        REGEXP_SUBSTR(attribute_value,'^\\\\d+') as amount,
        REPLACE(attribute_value, amount, '') as denom
      FROM osmosis.core.fact_msg_attributes att
      WHERE 
        msg_type = 'token_swapped'
    )
    GROUP BY
    	block_timestamp,
    	tx_id,
      	msg_group,
    	msg_index
)
GROUP BY 
  block_timestamp,
  tx_id,
  swapper,
  msg_group
ORDER BY
  block_timestamp,
  tx_id
),

v1_swap as (
SELECT
  block_timestamp,
  tx_id,
  swapper,
  msg_group,
  COUNT(DISTINCT pool_id) as n_pools,
  MAX(CASE WHEN msg_index = first_index THEN from_amount ELSE 0 END) as from_amount,
  MAX(CASE WHEN msg_index = first_index THEN from_denom ELSE '' END) as from_denom,
  MAX(CASE WHEN msg_index = last_index THEN to_amount ELSE 0 END) as to_amount,
  MAX(CASE WHEN msg_index = last_index THEN to_denom ELSE '' END) as to_denom
FROM (
  SELECT
      block_timestamp,
    	tx_id,
  		msg_group,
  		msg_index,
        FIRST_VALUE(msg_index) OVER (PARTITION BY msg_group, tx_id ORDER BY msg_index) as first_index,
        LAST_VALUE(msg_index) OVER (PARTITION BY msg_group, tx_id ORDER BY msg_index) as last_index,
    	MAX(IFF(LEN(sender) = 43, sender, '')) as swapper,
    	MAX(IFF(LEN(sender) = 43, recipient, '')) as pool_address,
    	MAX(
    		IFF(
      		LEN(sender) = 43, 
      		amount::integer, 
      		-1
    		)
  	)::integer as from_amount,
    	MAX(IFF(LEN(sender) = 43, denom, '')) as from_denom,
    	MAX(
    		IFF(
      		LEN(sender) > 43, 
      		amount::integer, 
      		-1
    		)
  	)::integer as to_amount,
    	MAX(IFF(LEN(sender) > 43, denom, '')) as to_denom
  FROM (
    SELECT
    		block_timestamp,
    		tx_id,
    		sender,
    		recipient,
  			msg_group,
    		IFF(LEN(sender) = 43, LEAD(msg_index) OVER (PARTITION BY tx_id ORDER BY msg_index), msg_index) as msg_index, -- adjust to the corresponding msg_index to prevent complex tx getting overwritten
      		REGEXP_SUBSTR(amount_ori,'^\\\\d+') as amount,
    		RIGHT(amount_ori, LEN(amount_ori) - LEN(amount::string)) as denom
    FROM (
    		SELECT 
    			block_timestamp,
            	att.tx_id,
            	att.msg_index,
  				att.msg_group,
            	MAX(IFF(attribute_key = 'sender', attribute_value, '')) as sender,
            	MAX(IFF(attribute_key = 'recipient', attribute_value, '')) as recipient,
            	MAX(IFF(attribute_key = 'amount', attribute_value, '')) as amount_ori
    		FROM osmosis.core.fact_msg_attributes att
    	  	WHERE
              msg_type = 'transfer'
  			  AND msg_group is not null
      		  AND EXISTS (SELECT 1
    			FROM osmosis.core.fact_msgs ms
    			WHERE 
    				tx_status = 'SUCCEEDED' 
    				AND (msg:attributes[0]:value = 'c3dhcF9leGFjdF9hbW91bnRfaW4=' OR msg:attributes[0]:value = 'c3dhcF9leGFjdF9hbW91bnRfb3V0')
  					and ms.tx_id = att.tx_id)
          GROUP BY
            block_timestamp,
            att.tx_id,
  			att.msg_group,
            att.msg_index
    		HAVING sender <> 'osmo1m5dncvfv7lvpvycr23zja93fecun2kcv226glq' -- claim contract
    )
  )
  GROUP BY
    block_timestamp,
    tx_id,
  	msg_group,
    msg_index
  ) s
  LEFT JOIN pool_ids i
  ON s.pool_address = i.pool_address
GROUP BY 
  block_timestamp,
  tx_id,
  swapper,
  msg_group
ORDER BY
  block_timestamp,
  tx_id
)

SELECT 
  	block_timestamp,
  	tx_id,
  	swapper,
  	SUM(from_amount) as from_amount,
  	from_denom,
  	SUM(to_amount) as to_amount,
  	to_denom,
  	SUM(to_amount - from_amount) as mev_amount,
  	SUM(to_amount - from_amount) * p.price_usd as mev_amount_usd
FROM (
  SELECT 
    block_timestamp,
    tx_id,
    swapper,
    from_amount / POW(10, NVL(l.raw_metadata[1]:exponent, 0)) as from_amount,
    NVL(l.project_name, from_denom) as from_denom,
    to_amount / POW(10, NVL(l.raw_metadata[1]:exponent, 0)) as to_amount,
    NVL(l.project_name, to_denom) as to_denom
  FROM (
    SELECT * FROM v2_swap
    UNION
    SELECT * FROM v1_swap
  )
  LEFT JOIN osmosis.core.dim_labels l
  ON l.address = from_denom
  WHERE from_denom = to_denom and n_pools > 1
) a
LEFT JOIN token_prices p
ON p.symbol = a.from_denom and p.date = a.block_timestamp::date
WHERE swapper = '{{swapper}}'
GROUP BY block_timestamp, tx_id, swapper, from_denom, to_denom, p.price_usd
ORDER BY mev_amount_usd DESC`;

const RESULT_PER_PAGE = 10;

const Searcher = () => {
    const [mevSummaryData, setMevSummaryData] = useState<MEVSummaryDataResponse>([]);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [currentSort, setCurrentSort] = useState<SortBy>("mev_amount_usd");
    const [currentSortOrder, setCurrentSortOrder] = useState<"desc" | "asc">("desc");
	const { address } = useParams();

    const isQuerying = useRef(false);

    const maxPage = useMemo(() => {
        return Math.ceil((mevSummaryData.length) / RESULT_PER_PAGE) - 1;
    }, [mevSummaryData]);

    const pageData = useMemo(() => {
        if(mevSummaryData.length === 0) {
            return [];
        }

        let sorted = cloneObj<MEVSummaryDataResponse>(mevSummaryData);

        switch(currentSort) {
            case "date":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.block_timestamp > b.block_timestamp? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.block_timestamp < b.block_timestamp? 1 : -1);
                }
                break;
            case "denom":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.from_denom > b.from_denom? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.from_denom < b.from_denom? 1 : -1);
                }
                break;
            case "from_amount":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.from_amount > b.from_amount? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.from_amount < b.from_amount? 1 : -1);
                }
                break;
            case "to_amount":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.to_amount > b.to_amount? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.to_amount < b.to_amount? 1 : -1);
                }
                break;
            case "mev_amount_usd":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.mev_amount_usd > b.mev_amount_usd? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.mev_amount_usd < b.mev_amount_usd? 1 : -1);
                }
                break;
            case "swapper":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.swapper > b.swapper? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.swapper < b.swapper? 1 : -1);
                }
                break;
            case "tx_id":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.tx_id > b.tx_id? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.tx_id < b.tx_id? 1 : -1);
                }
                break;
        }

        return sorted.filter((x, index) => index >= page * RESULT_PER_PAGE && index < (page + 1) * RESULT_PER_PAGE);
    }, [page, mevSummaryData, currentSort, currentSortOrder,]); 

    useEffect(() => {
        if(!address) {
            return;
        }

        if(isQuerying.current) {
            return;
        }

        isQuerying.current = true;

        const getData = async() => {
            let query = Mustache.render(txQuery, { swapper: address});
            try {
                let res = await queryTillSuccess(query);
                setMevSummaryData(res as MEVSummaryDataResponse);
            }

            catch(e) {
                console.log(e);
            }
            setIsLoading(false);
        }
        
        getData();
    }, [address]);

    const increasePage = useCallback(() => {
        let newPage = page + 1;
        if(newPage > maxPage) {
            return;
        }
        setPage(newPage);
    }, [page, maxPage]);

    const decreasePage = useCallback(() => {
        let newPage = page - 1;
        if(newPage < 0) {
            return;
        }
        setPage(newPage);
    }, [page]);

    const sortBy = useCallback((sortBy: SortBy) => {
        if(currentSort === sortBy) {
            setCurrentSortOrder(currentSortOrder === "asc"? "desc" : "asc");
        }

        else {
            setCurrentSort(sortBy);
            setCurrentSortOrder("desc");
        }
    }, [currentSort, currentSortOrder]);

    return (
        <div className="searcher-page">
            {
                pageData.length > 0 &&
                <div className="result-table">
                    <div className="table-navigator">
                        <strong>Searcher: {address}</strong>
                        <div className="d-flex align-items-center">
                            <button onClick={decreasePage}><i className="fa fa-chevron-left"></i></button>
                            <span>{page + 1} / {maxPage + 1}</span>
                            <button onClick={increasePage}><i className="fa fa-chevron-right"></i></button>
                        </div>
                    </div>
                    <table className='table table-striped'>
                        <thead>
                            <tr>
                                <th>
                                    <button onClick={() => sortBy("date")}>
                                        <div>Date</div>
                                        {
                                            currentSort === "date" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("tx_id")}>
                                        <div>TX ID</div>
                                        {
                                            currentSort === "tx_id" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("swapper")}>
                                        <div>Searcher</div>
                                        {
                                            currentSort === "swapper" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("denom")}>
                                        <div>Symbol</div>
                                        {
                                            currentSort === "denom" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("from_amount")}>
                                        <div>From Amount</div>
                                        {
                                            currentSort === "from_amount" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("to_amount")}>
                                        <div>To Amount</div>
                                        {
                                            currentSort === "to_amount" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"} ms-2`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("mev_amount_usd")}>
                                        <div>Profit</div>
                                        {
                                            currentSort === "mev_amount_usd" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"} ms-2`}></i>
                                        }
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                pageData.map((x, index) => (
                                    <tr key={`explorer-data-${index}`}>
                                        <td>{moment(x.block_timestamp).format('YYYY-MM-DD')}<br/>{moment(x.block_timestamp).format('HH:mm:ss')}</td>
                                        <td><a className='link' href={`/tx/${x.tx_id}`} rel="noopener noreferrer" target="_blank">{ellipsizeThis(x.tx_id, 6, 6)}</a></td>
                                        <td><a className='link' href={`/searcher/${x.swapper}`} rel="noopener noreferrer" target="_blank">{ellipsizeThis(x.swapper, 6, 6)}</a></td>
                                        <td>{x.from_denom}</td>
                                        <td>{toLocaleDecimal(x.from_amount, 2, 2)}</td>
                                        <td>{toLocaleDecimal(x.to_amount, 2, 2)}</td>
                                        <td>{toLocaleDecimal(x.mev_amount, 2, 2)}<br/>(${toLocaleDecimal(x.mev_amount_usd, 2, 2)})</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>

            }
            
            <Spinner
                type='pulse'
                mode="dark"
                text="Loading"
                show={isLoading}
                fullScreen
            />  
        </div>
    );
}

export default Searcher;